(ns app.front.view.workspace.location
  "Events, subscriptions, components and other functions related
  to editing workspace location (which is different from properties)."
  (:require [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.util :as futil]
            [app.front.view.navigation :as navigation]
            [app.front.view.workspace.map :as workspace-map]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(def state-path (rf/path [::state]))

(defn- add-workspace-geometry [workspace-geometry drawing-geometry]
  (if (-> workspace-geometry :geometries empty?)
    {:type       "GeometryCollection"
     :geometries [drawing-geometry]}
    (update workspace-geometry :geometries conj drawing-geometry)))

(defn- edit-workspace-geometry
  "Edit or delete a geometry from the workspace geometries.
  If `id` is nil (unknown), return unmodified geometry.
  If `drawing-geometry` is nil, delete the geometry with the
  given id, otherwise replace it with the given geometry."
  [workspace-geometry drawing-geometry id]
  (if-not id
    workspace-geometry
    (let [new-geometries (->> workspace-geometry
                              :geometries
                              (keep-indexed (fn [idx geometry]
                                              (if (= (str idx) id)
                                                drawing-geometry
                                                geometry)))
                              vec)]
      (assoc workspace-geometry :geometries new-geometries))))

(rf/reg-event-fx ::open
  (fn [{:keys [db]} _]
    {:db (assoc db ::state {:open? true})
     :fx [[:dispatch [::workspace-map/set-map-extended true]]
          [:dispatch [::navigation/scroll-to-top]]]}))

(rf/reg-event-fx ::close
  (fn [{:keys [db]} _]
    {:db       (dissoc db ::state)
     :dispatch [::workspace-map/set-map-extended false]}))

(rf/reg-event-db ::clear-state
  (fn [db _]
    (dissoc db ::state)))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::open?
  :<- [::state]
  :-> :open?)

(rf/reg-sub ::modified-workspace-geometry
  :<- [::state]
  :-> :modified-workspace-geometry)

(rf/reg-sub ::manual-coordinates
  :<- [::state]
  :-> :coordinates)

(rf/reg-event-db ::set-manual-location-coordinates
  state-path
  (fn [state [_ coordinates]]
    (assoc state :coordinates coordinates)))

(rf/reg-event-db ::set-change-location-center
  state-path
  (fn [state _]
    (update state :change-location-center not)))

(rf/reg-sub ::change-location-center?
  :<- [::state]
  :-> :change-location-center)

(rf/reg-sub ::no-geometry-modifications?
  :<- [::modified-workspace-geometry]
  :<- [::manual-coordinates]
  (fn [[geometry coordinates] _]
    (and (empty? geometry)
         (empty? coordinates))))

(rf/reg-sub ::modified-location-description
  :<- [::state]
  :-> :modified-location-description)

(rf/reg-sub ::no-modifications?
  :<- [::no-geometry-modifications?]
  :<- [::modified-location-description]
  (fn [[geometry? description] _]
    (and geometry? (nil? description))))

(rf/reg-event-db ::edit-location-description
  state-path
  (fn [state [_ desc]]
    (assoc state :modified-location-description desc)))

(rf/reg-event-db ::edit-workspace-geometry
  state-path
  (fn [state [_ action original-workspace-geometry {:keys [id geometry] :as _drawing}]]
    (let [ws-geometry   (or (:modified-workspace-geometry state)
                            original-workspace-geometry)
          new-geometry  (case action
                          :workspace-location/add
                          (add-workspace-geometry ws-geometry geometry)

                          (:workspace-location/edit :workspace-location/delete)
                          (edit-workspace-geometry ws-geometry geometry id)

                          ws-geometry)
          empty->empty? (and (-> original-workspace-geometry :geometries empty?)
                             (-> new-geometry :geometries empty?))]
      (cond-> state
        (not empty->empty?) (assoc :modified-workspace-geometry new-geometry)
        empty->empty? (dissoc :modified-workspace-geometry)
        true (dissoc :selected-feature)))))

(rf/reg-event-db ::clear-modifications
  state-path
  (fn [state _]
    (dissoc state
            :modified-workspace-geometry
            :coordinates
            :selected-feature)))

(rf/reg-event-db ::select-feature
  state-path
  (fn [state [_ feature-id]]
    (if feature-id
      (assoc state :selected-feature feature-id)
      (dissoc state :selected-feature))))

(rf/reg-sub ::selected-feature
  :<- [::state]
  :-> :selected-feature)

(rf/reg-event-fx ::save-modified-geometry
  (fn [{:keys [db]} [_ target-id]]
    {:db       (assoc-in db [::state :saving?] true)
     :dispatch [::process/signal
                target-id
                [:process.signal/edit-workspace-location
                 (p/assoc-if {}
                             :geometry    (-> db ::state :modified-workspace-geometry)
                             :description (-> db ::state :modified-location-description)
                             :coordinates (-> db ::state :coordinates))]
                {:on-success [::clear-modifications]
                 :on-done    [::save-modified-geometry.done]
                 :notify     {:success :workspace/location-updated}}]}))

(rf/reg-event-db ::save-modified-geometry.done
  state-path
  (fn [state _]
    (dissoc state :saving?)))

(rf/reg-sub ::saving?
  :<- [::state]
  :-> :saving?)

(rf/reg-sub ::action-buttons-disabled?
  :<- [::no-modifications?]
  :<- [::saving?]
  (fn [[no-modifications? saving?] _]
    (boolean (or no-modifications? saving?))))

(rf/reg-sub ::unlinking-in-progress?
  :<- [::state]
  :-> ::unlinking-in-progress?)

(rf/reg-event-db ::unlink-primary-property.done
  state-path
  (fn [state _]
    (dissoc state ::unlinking-in-progress?)))

(rf/reg-event-fx ::unlink-primary-property
  (fn [{:keys [db]} [_ target-id]]
    {:db       (assoc-in db [::state ::unlinking-in-progress?] true)
     :dispatch [::process/signal
                target-id
                [:process.signal/unlink-primary-property]
                {:on-done [::unlink-primary-property.done]
                 :notify  {:success :workspace/property-changed}}]}))

(defn coordinates [tr location]
  (let [coordinates (futil/lat-long location)]
    [:div
     [:label.bx--label
      (tr [:location/map-pin-coordinates])]
     [:div.flex.items-center.gap-2
      [ui/number-input {:label-text (tr [:location/latitude])
                        :read-only  true
                        :value      (:latitude coordinates)}]
      [ui/number-input {:label-text (tr [:location/longitude])
                        :read-only  true
                        :value      (:longitude coordinates)}]]]))

(defn location-description [tr value]
  [ui/text-area {:label-text (tr [:location/description])
                 :value      value
                 :on-change  #(>evt [::edit-location-description (futil/tval %)])}])

(defn- view [{:keys [tr target-id workspace]}]
  (let [buttons-disabled?             (<sub [::action-buttons-disabled?])
        location                      (:entity/location workspace)
        workspace-geometry            (:location/shape location)
        unlink-property-visible?      (<sub [::process/signal-permitted?
                                             target-id
                                             [:process.signal/unlink-primary-property]])
        selected-feature              (<sub [::selected-feature])
        modified-location-description (<sub [::modified-location-description])
        manual-coordinates            (<sub [::manual-coordinates])
        local-coordinates             (if manual-coordinates (into [] manual-coordinates) location)]
    [:div.bg-ui-white.p-4
     [:div.pr-3.mb-6
      [:span.font-bold (tr [:workspace/edit-workspace-location-help])]]
     [:div.flex.flex-row.mb-6
      [:div.w-full.mr-8
       [location-description tr (or modified-location-description (:location/description location))]]
      [coordinates tr local-coordinates]]
     [:div.flex.justify-end.pt-4
      [ui-action-buttons/action-buttons
       {:buttons [{:disabled? buttons-disabled?
                   :on-click  #(>evt [::clear-modifications])
                   :text      (tr [:cancel-changes])}
                  {:disabled? (or (nil? selected-feature)
                                  (<sub [::saving?]))
                   :icon      i/trash
                   :on-click  #(do
                                 (>evt [::set-manual-location-coordinates nil])
                                 (>evt [::edit-workspace-geometry
                                        :workspace-location/delete
                                        workspace-geometry
                                        {:id selected-feature}]))
                   :text      (tr [:workspace/delete-selected])}
                  {:on-click #(>evt [::set-change-location-center])
                   :text     (if (<sub [::change-location-center?])
                               (tr [:workspace/change-location-center-cancel])
                               (tr [:workspace/change-location-center]))}
                  {:on-click #(>evt [::close])
                   :text     (tr [:cancel])}
                  (when unlink-property-visible?
                    {:on-click #(>evt [::unlink-primary-property target-id])
                     :disable  (<sub [::unlinking-in-progress?])
                     :text     (tr [:process.signal/unlink-primary-property])})
                  {:disabled? buttons-disabled?
                   :icon      i/building
                   :on-click  #(>evt [::save-modified-geometry target-id])
                   :text      (tr [:workspace/save-workspace-location])}]}]]]))

(defn edit-workspace-geometry-widget [props]
  (when (<sub [::open?])
    [view props]))
